import { useEffect, useState } from 'react';
import { CalendarClock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { datetimePickerHandlers } from '@/lib/datetime-picker';

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (scheduledAt?: string) => void;
  isLoading?: boolean;
}

type Mode = 'now' | 'schedule';

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Common scheduling targets for newsroom workflow — most posts get
 * scheduled either for the next news cycle (morning / evening) or the
 * start of the next workweek. Quick-select chips skip the date-picker
 * juggling that a datetime input alone forces.
 */
interface Preset {
  label: string;
  compute: () => Date;
}

function startOfNextDayAt(hour: number, minute = 0): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, minute, 0, 0);
  return d;
}

function nextMondayAt(hour: number, minute = 0): Date {
  const d = new Date();
  // 0 = Sun, 1 = Mon, ..., 6 = Sat. If today IS Monday, jump to next
  // Monday (offset 7) — same day at past 09:00 is rarely what admin wants.
  const dow = d.getDay();
  const offset = dow === 1 ? 7 : (1 - dow + 7) % 7 || 7;
  d.setDate(d.getDate() + offset);
  d.setHours(hour, minute, 0, 0);
  return d;
}

const PRESETS: Preset[] = [
  { label: '+1 ชม.', compute: () => new Date(Date.now() + 60 * 60 * 1000) },
  { label: 'พรุ่งนี้ 9:00', compute: () => startOfNextDayAt(9) },
  { label: 'พรุ่งนี้ 18:00', compute: () => startOfNextDayAt(18) },
  { label: 'จันทร์ 9:00', compute: () => nextMondayAt(9) },
];

export function PublishDialog({ open, onOpenChange, onConfirm, isLoading }: PublishDialogProps) {
  const [mode, setMode] = useState<Mode>('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode('now');
      // Default to 1 hour from now, local time
      const d = new Date(Date.now() + 60 * 60 * 1000);
      setScheduledAt(toDatetimeLocalValue(d));
      setError(null);
    }
  }, [open]);

  const handleConfirm = () => {
    if (mode === 'now') {
      onConfirm();
      return;
    }
    if (!scheduledAt) {
      setError('กรุณาเลือกวันเวลา');
      return;
    }
    const date = new Date(scheduledAt);
    if (Number.isNaN(date.getTime())) {
      setError('รูปแบบวันเวลาไม่ถูกต้อง');
      return;
    }
    if (date.getTime() <= Date.now()) {
      setError('เวลาต้องเป็นอนาคต');
      return;
    }
    onConfirm(date.toISOString());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>เผยแพร่บทความ</DialogTitle>
          <DialogDescription>เลือกเผยแพร่ทันที หรือกำหนดเวลาเผยแพร่ในอนาคต</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <label className="hover:border-brand-400 flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-800">
            <input
              type="radio"
              name="publish-mode"
              checked={mode === 'now'}
              onChange={() => setMode('now')}
              className="h-4 w-4"
            />
            <Send className="h-5 w-5 text-slate-500" />
            <div className="flex-1">
              <div className="text-sm font-medium">เผยแพร่ทันที</div>
              <div className="text-xs text-slate-500">บทความจะปรากฏให้ผู้อ่านทันที</div>
            </div>
          </label>

          <label className="hover:border-brand-400 flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-800">
            <input
              type="radio"
              name="publish-mode"
              checked={mode === 'schedule'}
              onChange={() => setMode('schedule')}
              className="h-4 w-4"
            />
            <CalendarClock className="h-5 w-5 text-slate-500" />
            <div className="flex-1">
              <div className="text-sm font-medium">ตั้งเวลาเผยแพร่</div>
              <div className="text-xs text-slate-500">บทความจะถูกเผยแพร่ตามเวลาที่กำหนด</div>
            </div>
          </label>

          {mode === 'schedule' && (
            <div className="space-y-2 pl-7">
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => {
                      setScheduledAt(toDatetimeLocalValue(p.compute()));
                      setError(null);
                    }}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <Label htmlFor="scheduledAt" className="text-xs">
                วันเวลาเผยแพร่
              </Label>
              <Input
                id="scheduledAt"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => {
                  setScheduledAt(e.target.value);
                  setError(null);
                }}
                {...datetimePickerHandlers}
                className="cursor-pointer"
              />
              {error && <p className="text-destructive text-xs">{error}</p>}
              <p className="text-[11px] text-slate-500">
                เวลาตามเครื่องคุณ — ระบบจะเก็บเป็น UTC อัตโนมัติ
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            ยกเลิก
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'กำลังดำเนินการ...' : mode === 'now' ? 'เผยแพร่ทันที' : 'ตั้งเวลา'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
